import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService, prospectService } from '../services/api';
import { ProspectsResponse } from '../types';
import { usePipelineStages } from '../services/usePipelineStages';
import { useAuth } from '../contexts/AuthContext';

const ProspectsList: React.FC = () => {
  const navigate = useNavigate();
  const { user, impersonate } = useAuth();
  const [data, setData] = useState<ProspectsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [sendingResetId, setSendingResetId] = useState<number | null>(null);
  const [sentResetIds, setSentResetIds] = useState<Set<number>>(new Set());
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetTargetId, setResetTargetId] = useState<number | null>(null);
  const [resetTargetEmail, setResetTargetEmail] = useState<string>('');
  const [resetTargetName, setResetTargetName] = useState<string>('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetExpiresMinutes, setResetExpiresMinutes] = useState('30');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLead, setNewLead] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importUserJson, setImportUserJson] = useState('');
  const [importProspectJson, setImportProspectJson] = useState('');
  const [keepLegacyPasswordHash, setKeepLegacyPasswordHash] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');

  const [pipelineStages] = usePipelineStages();
  const stages = [
    { code: '', name: 'All Stages' },
    ...pipelineStages.map((stage: any) => ({
      code: stage.code,
      name: stage.title || stage.name || stage.code
    }))
  ];

  // Helper to get stage info from code
  const getStageInfo = (code: string) => pipelineStages.find((s: any) => s.code === code);

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await prospectService.getProspects(
        currentPage,
        20,
        stageFilter || undefined,
        search || undefined
      );
      setData(response);
    } catch (err) {
      setError('Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [currentPage, stageFilter, search]);

  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProspects();
  };

  const handleStageFilter = (stage: string) => {
    setStageFilter(stage);
    setCurrentPage(1);
  };
  
  const handleImpersonate = async (userId?: number) => {
    if (!userId) {
      setError('Cannot impersonate a prospect without a user account.');
      return;
    }
    setNotice('');
    setError('');
    const result = await impersonate(userId);
    if (!result.success || !result.user) {
      setError('Failed to impersonate user');
      return;
    }
    if (result.user.role === 'professor') {
      navigate('/professor-pathway');
    } else {
      navigate('/student-pathway');
    }
  };

  const handleSendReset = async (id: number, email: string, message?: string, expiresMinutes?: number) => {
    if (!email) {
      setError('No email available for this prospect.');
      return;
    }
    setError('');
    setNotice('');
    try {
      setSendingResetId(id);
      await authService.requestAdminPasswordReset({
        email,
        message,
        expires_minutes: expiresMinutes,
      });
      setNotice(`Password reset link sent to ${email}.`);
      setSentResetIds((prev) => new Set(prev).add(id));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to send reset link.');
    } finally {
      setSendingResetId(null);
    }
  };

  const openResetModal = (id: number, email?: string | null, name?: string) => {
    if (!email) {
      setError('No email available for this prospect.');
      return;
    }
    setError('');
    setNotice('');
    setResetTargetId(id);
    setResetTargetEmail(email);
    setResetTargetName(name || '');
    setResetMessage('');
    setResetExpiresMinutes('30');
    setResetModalOpen(true);
  };

  const closeResetModal = () => {
    setResetModalOpen(false);
    setResetTargetId(null);
    setResetTargetEmail('');
    setResetTargetName('');
  };

  const openCreateModal = () => {
    setError('');
    setNotice('');
    setNewLead({
      first_name: '',
      last_name: '',
      email: '',
    });
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    if (!creating) {
      setCreateModalOpen(false);
    }
  };

  const submitCreateLead = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setCreating(true);
    try {
      await prospectService.createProspect({
        first_name: newLead.first_name.trim(),
        last_name: newLead.last_name.trim(),
        email: newLead.email.trim(),
      });
      setNotice(`Lead created for ${newLead.email.trim()}.`);
      setCreateModalOpen(false);
      fetchProspects();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to create lead.');
    } finally {
      setCreating(false);
    }
  };

  const openImportModal = () => {
    setError('');
    setNotice('');
    setImportUserJson('');
    setImportProspectJson('');
    setKeepLegacyPasswordHash(true);
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    if (!importing) {
      setImportModalOpen(false);
    }
  };

  const submitImportLegacy = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    let userPayload: any;
    let prospectPayload: any;
    try {
      userPayload = JSON.parse(importUserJson);
    } catch {
      setError('Invalid User JSON.');
      return;
    }
    try {
      prospectPayload = JSON.parse(importProspectJson);
    } catch {
      setError('Invalid Prospect JSON.');
      return;
    }
    setImporting(true);
    try {
      const response = await authService.importLegacyUser({
        user: userPayload,
        prospect: prospectPayload,
        keep_password_hash: keepLegacyPasswordHash,
      });
      setNotice(`Imported legacy user (user_id ${response.user_id}).`);
      setImportModalOpen(false);
      fetchProspects();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Unable to import legacy user.');
    } finally {
      setImporting(false);
    }
  };

  const submitResetModal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetTargetId || !resetTargetEmail) return;
    const minutes = Number(resetExpiresMinutes);
    const expiresMinutes = Number.isNaN(minutes) ? 30 : minutes;
    await handleSendReset(resetTargetId, resetTargetEmail, resetMessage, expiresMinutes);
    closeResetModal();
  };

  if (loading && !data) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="bg-gray-200 h-64 rounded-2xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-2 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Prospects Management</h2>
          <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">Track and manage your study abroad prospects</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-6 py-3"
          >
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add New Visitor
          </button>
          <button
            onClick={openImportModal}
            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-6 py-3"
          >
            Import Legacy
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Search */}
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Search prospects</h3>
          </div>
          <form onSubmit={handleSearch} className="flex space-x-3 p-4">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </button>
          </form>
        </div>

        {/* Stage Filters */}
        <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Filter by stage</h3>
          </div>
          <div className="flex flex-wrap gap-2 p-4">
            {stages.map((stage) => (
              <button
                key={stage.code}
                onClick={() => handleStageFilter(stage.code)}
                className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors px-4 py-2 text-sm ${
                  stageFilter === stage.code
                    ? 'bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:border-blue-700'
                    : 'bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                {stage.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-md mb-6">
          {notice}
        </div>
      )}

      {/* Prospects Table */}
      <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Prospects</h3>
          <p className="text-xs text-gray-500">{data?.pagination?.total || 0} total</p>
        </div>
        {data?.prospects && data.prospects.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800 border-b border-gray-200 dark:border-slate-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Phone</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Stage</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Program Interest</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Created</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-slate-100">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {data.prospects.map((prospect) => (
                    <tr key={prospect.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/60 transition duration-150">
                      <td className="px-6 py-4">
                        <Link
                          to={`/admin/prospects/${prospect.id}`}
                          className="text-blue-600 hover:text-blue-800 font-semibold transition duration-200"
                        >
                          {prospect.first_name} {prospect.last_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{prospect.email || '-'}</td>
                      <td className="px-6 py-4 text-gray-700">{prospect.phone || '-'}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800 dark:bg-slate-700 dark:text-slate-100">
                          {getStageInfo(prospect.current_stage)?.title || prospect.current_stage}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{prospect.program_interest || '-'}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm">
                        {new Date(prospect.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 flex space-x-2">
                        <Link
                          to={`/admin/prospects/${prospect.id}`}
                          className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-1 text-sm"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        </Link>
                        {user?.is_admin && (
                          <button
                            onClick={() => handleImpersonate(prospect.user_id)}
                            className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-amber-500 text-amber-950 border border-amber-500 hover:bg-amber-600 hover:border-amber-600 px-3 py-1 text-sm"
                            title="View as user"
                            disabled={!prospect.user_id}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A10 10 0 1118.88 6.195M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => openResetModal(
                            prospect.id,
                            prospect.email,
                            `${prospect.first_name} ${prospect.last_name}`.trim()
                          )}
                          className={`inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors px-3 py-1 text-sm ${
                            sentResetIds.has(prospect.id)
                              ? 'bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700'
                              : 'bg-slate-900 text-white border border-slate-900 hover:bg-slate-800 hover:border-slate-800'
                          }`}
                          title={sentResetIds.has(prospect.id) ? 'Reset link sent' : 'Send password reset link'}
                          aria-label="Send password reset link"
                          disabled={sendingResetId === prospect.id}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10V6a2 2 0 10-4 0v4m8 0V8a4 4 0 00-8 0v2m8 0h1a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2v-6a2 2 0 012-2h1m4 4v2" />
                          </svg>
                          {sendingResetId === prospect.id ? 'Sending…' : sentResetIds.has(prospect.id) ? 'Sent' : 'Send'}
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this prospect? This action cannot be undone.')) {
                              try {
                                await prospectService.deleteProspect(prospect.id);
                                fetchProspects();
                              } catch (err) {
                                setError('Failed to delete prospect');
                              }
                            }
                          }}
                          className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-red-500 text-white border border-red-500 hover:bg-red-600 hover:border-red-600 px-3 py-1 text-sm"
                          title="Delete Prospect"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.pagination.pages > 1 && (
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <nav className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((data.pagination.page - 1) * data.pagination.per_page) + 1} to{' '}
                    {Math.min(data.pagination.page * data.pagination.per_page, data.pagination.total)} of{' '}
                    {data.pagination.total} results
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!data.pagination.has_prev}
                      className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-blue-600 text-white border border-blue-600 hover:bg-blue-700 hover:border-blue-700 px-3 py-2 text-sm">
                      {data.pagination.page}
                    </span>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!data.pagination.has_next}
                      className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No prospects found</h3>
            <p className="text-gray-600 mb-6">
              {search || stageFilter
                ? 'No prospects match your current filters. Try adjusting your search criteria.'
                : 'Start building your pipeline by adding your first visitor through the capture form.'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-6 py-3"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add First Visitor
            </Link>
          </div>
        )}
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white/85 dark:bg-slate-900/70 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_14px_36px_rgba(15,23,42,0.12)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">Loading prospects...</span>
          </div>
        </div>
      )}

      {resetModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/60" onClick={closeResetModal} />
          <div className="relative z-10 w-full max-w-lg bg-white/90 dark:bg-slate-900/80 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_20px_48px_rgba(15,23,42,0.24)] dark:shadow-[0_24px_56px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Send reset link</h3>
                <p className="text-xs text-slate-500 dark:text-slate-300">
                  {resetTargetName ? `${resetTargetName} • ` : ''}{resetTargetEmail}
                </p>
              </div>
              <button onClick={closeResetModal} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100" aria-label="Close">
                ✕
              </button>
            </div>
            <form onSubmit={submitResetModal} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Custom message (optional)</label>
                <textarea
                  value={resetMessage}
                  onChange={(e) => setResetMessage(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                  placeholder="Add context so the recipient knows this is legit."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Link expiry (minutes)</label>
                <input
                  type="number"
                  min={5}
                  max={1440}
                  value={resetExpiresMinutes}
                  onChange={(e) => setResetExpiresMinutes(e.target.value)}
                  className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Min 5, max 1440 (24 hours).</p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeResetModal}
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
                >
                  Send reset link
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/60" onClick={closeCreateModal} />
          <div className="relative z-10 w-full max-w-lg bg-white/90 dark:bg-slate-900/80 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_20px_48px_rgba(15,23,42,0.24)] dark:shadow-[0_24px_56px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Create new visitor</h3>
                <p className="text-xs text-slate-500 dark:text-slate-300">Creates a new lead like the public hero form.</p>
              </div>
              <button onClick={closeCreateModal} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100" aria-label="Close">
                ✕
              </button>
            </div>
            <form onSubmit={submitCreateLead} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">First name</label>
                  <input
                    required
                    value={newLead.first_name}
                    onChange={(e) => setNewLead((prev) => ({ ...prev, first_name: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Last name</label>
                  <input
                    required
                    value={newLead.last_name}
                    onChange={(e) => setNewLead((prev) => ({ ...prev, last_name: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Email</label>
                <input
                  required
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
                  disabled={creating}
                >
                  {creating ? 'Creating…' : 'Create visitor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {importModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/60" onClick={closeImportModal} />
          <div className="relative z-10 w-full max-w-2xl bg-white/90 dark:bg-slate-900/80 border border-slate-100/90 dark:border-slate-700/80 rounded-3xl shadow-[0_20px_48px_rgba(15,23,42,0.24)] dark:shadow-[0_24px_56px_rgba(0,0,0,0.55)] backdrop-blur-[18px] overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100/80 dark:border-slate-700/80 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50">Import legacy user</h3>
                <p className="text-xs text-slate-500 dark:text-slate-300">Paste user + prospect JSON from the legacy database.</p>
              </div>
              <button onClick={closeImportModal} className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100" aria-label="Close">
                ✕
              </button>
            </div>
            <form onSubmit={submitImportLegacy} className="p-6 space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">User JSON</label>
                  <textarea
                    value={importUserJson}
                    onChange={(e) => setImportUserJson(e.target.value)}
                    rows={10}
                    className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                    placeholder='{"email": "...", "password_hash": "..."}'
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">Prospect JSON</label>
                  <textarea
                    value={importProspectJson}
                    onChange={(e) => setImportProspectJson(e.target.value)}
                    rows={10}
                    className="w-full rounded-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/80 dark:bg-slate-950/40 text-slate-900 dark:text-slate-100 px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                    placeholder='{"first_name": "...", "current_stage": "..."}'
                    required
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={keepLegacyPasswordHash}
                  onChange={(e) => setKeepLegacyPasswordHash(e.target.checked)}
                />
                Keep legacy password hash (default)
              </label>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeImportModal}
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-white/80 dark:bg-slate-900/60 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2"
                  disabled={importing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 font-semibold rounded-lg transition-colors bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 px-4 py-2"
                  disabled={importing}
                >
                  {importing ? 'Importing…' : 'Import'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectsList;
